/**
 * Browser-side admin client — Supabase-compatible API over our admin CRUD proxy.
 *
 * Pages call `supabase.from("table").select().eq(...)` and the request goes to
 * `${VITE_API_URL}/api/v1/admin/query` with the admin JWT in the
 * Authorization header.
 *
 * This is the same shape Miluliem's admin uses — it lets us copy admin pages
 * 1:1 across client projects with only field/table-name adaptations.
 */

import { createRailwayClient } from "./railway-client.js";

const ADMIN_TOKEN_KEY = "vinoga_admin_token";

function resolveApiBase(): string {
  // CRITICAL: must use the literal `import.meta.env.VITE_API_URL` access
  // pattern so Vite static-substitutes the value at build time. The previous
  // version went through a `as unknown as` cast plus an optional chain — Vite's
  // substitution didn't follow through it, and the deployed bundle ended up
  // reading `import.meta.env` at runtime (where it's undefined in production
  // SPA bundles), silently falling back to "" and routing /api/v1/admin/query
  // requests to the admin's own origin (405 Method Not Allowed).
  //
  // We still want the package to be importable from non-Vite contexts (e.g.
  // node scripts) without crashing at parse time, so we use a try/catch.
  try {
    // CRITICAL: must keep the literal `import.meta.env.VITE_API_URL` access
    // pattern below — Vite does syntactic substitution at build time and
    // any cast / indirection breaks the replacement, leaving the runtime
    // value as `undefined`. The bundle then falls through to "" which
    // makes the admin's relative API calls hit the Pages host (e.g.
    // panelis.vinoga.lv) instead of api2.vinoga.lv → 405 Method Not
    // Allowed. The `// @ts-ignore` is intentional: this file is also
    // imported from Node scripts where `import.meta.env` doesn't exist
    // typewise. The try/catch handles the Node runtime case.
    // @ts-ignore — see comment above; do not refactor.
    const v = import.meta.env.VITE_API_URL;
    if (typeof v === "string" && v.length > 0) return v;
  } catch {
    // No Vite env — falls through to "" below.
  }
  return "";
}

function getToken(): string | null {
  if (typeof localStorage === "undefined") return null;
  return localStorage.getItem(ADMIN_TOKEN_KEY);
}

export const supabase = createRailwayClient(resolveApiBase(), getToken);
export default supabase;
